import { useEffect, useRef, useState } from "react";
import { isTouchDevice, prefersReducedMotion } from "../../utils/deviceDetection";
import "../../styles/CursorFollower.css";

const CURSOR_STATES = {
  DEFAULT: "default",
  HOVER: "hover",
  TEXT: "text",
  IMAGE: "image"
};

function CursorFollower() {
  const targetPos = useRef({ x: 0, y: 0 });
  const currentPos = useRef({ x: 0, y: 0 });
  const followerRef = useRef(null);
  const animationFrameId = useRef(null);
  const [cursorState, setCursorState] = useState(CURSOR_STATES.DEFAULT);
  const cursorStateRef = useRef(CURSOR_STATES.DEFAULT);

  const isTouch = isTouchDevice();
  const isReducedMotion = prefersReducedMotion();

  useEffect(() => {
    if (isTouch || isReducedMotion) return;

    const handlePointerMove = (e) => {
      targetPos.current = { x: e.clientX, y: e.clientY };

      const target = e.target instanceof Element ? e.target : null;
      if (!target) {
        return;
      }

      const isHoverable =
        target.closest("a") ||
        target.closest("button") ||
        target.closest("[role='button']") ||
        target.closest("[data-cursor='hover']");

      const isTextInput =
        target.closest("input") ||
        target.closest("textarea") ||
        target.closest("[contenteditable='true']");

      const isImage =
        target.closest("img") ||
        target.closest("picture") ||
        target.closest("video");

      let newState = CURSOR_STATES.DEFAULT;
      if (isHoverable) {
        newState = CURSOR_STATES.HOVER;
      } else if (isTextInput) {
        newState = CURSOR_STATES.TEXT;
      } else if (isImage) {
        newState = CURSOR_STATES.IMAGE;
      }

      // Only trigger re-render when cursor state actually changes
      if (cursorStateRef.current !== newState) {
        cursorStateRef.current = newState;
        setCursorState(newState);
      }
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: true });

    const animate = () => {
      const lerpFactor = cursorStateRef.current === CURSOR_STATES.HOVER ? 0.2 : 0.14;

      currentPos.current.x +=
        (targetPos.current.x - currentPos.current.x) * lerpFactor;
      currentPos.current.y +=
        (targetPos.current.y - currentPos.current.y) * lerpFactor;

      if (followerRef.current) {
        followerRef.current.style.setProperty("--cursor-x", `${currentPos.current.x}px`);
        followerRef.current.style.setProperty("--cursor-y", `${currentPos.current.y}px`);
      }

      animationFrameId.current = requestAnimationFrame(animate);
    };

    animationFrameId.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [isTouch, isReducedMotion]);

  // Don't render on touch devices
  if (isTouch || isReducedMotion) return null;

  return (
    <div
      className={`cursor-follower cursor-follower--${cursorState}`}
      ref={followerRef}
      aria-hidden="true"
    >
      <div className="cursor-follower__inner" />
    </div>
  );
}

export default CursorFollower;
